package com.wintermar.inspection.feature.presentation.inspection.answer.search

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.TextBodyTiny
import com.wintermar.inspection.core.ui.component.button.IconButton
import com.wintermar.inspection.core.ui.component.datetime.DateTimePickerModal
import com.wintermar.inspection.core.ui.component.image.ImagePickerModal
import com.wintermar.inspection.core.ui.component.input.InputSearchText
import com.wintermar.inspection.core.ui.component.location.LocationModal
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.core.ui.util.SelectionModel
import com.wintermar.inspection.core.ui.util.highlightBoldKeyword
import com.wintermar.inspection.core.ui.util.highlightKeyword
import com.wintermar.inspection.core.ui.util.toTitleCase
import com.wintermar.inspection.core.ui.widget.DefaultHorizontalDivider
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.presentation.inspection.answer.search.util.InspectionSearchQuestionType
import com.wintermar.inspection.feature.presentation.inspection.answer.search.widget.InspectionSearchResultItem
import com.wintermar.inspection.feature.presentation.inspection.answer.search.widget.InspectionSearchResultTab
import com.wintermar.inspection.feature.presentation.inspection.answer.setupContent
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.ContentItem
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.ContentItemProps
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.finding_category.FindingCategoryModal
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.guidance.GuidanceModal
import com.wintermar.inspection.feature.presentation.inspection.answer.widget.notes.NotesModal
import com.wintermar.inspection.feature.util.finding_category.toVariant
import com.wintermar.inspection.util.Constant
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.add_files
import inspection.composeapp.generated.resources.ic_arrow_left_line
import inspection.composeapp.generated.resources.initial_search_question
import inspection.composeapp.generated.resources.maximum_file
import inspection.composeapp.generated.resources.search_no_result
import inspection.composeapp.generated.resources.search_result
import inspection.composeapp.generated.resources.search_this_audit
import inspection.composeapp.generated.resources.select_category
import inspection.composeapp.generated.resources.select_location
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun InspectionSearchQuestionScreen(
    paddingValues: PaddingValues,
    inspectionTemplate: InspectionTemplateModel,
    viewModel: InspectionSearchQuestionViewModel = koinViewModel { parametersOf(inspectionTemplate) },
    color: ThemeColor = LocalAppTheme.current.color,
    dimension: AppDimension = LocalAppTheme.current.dimension,
    onClose: (
        data: InspectionTemplateModel,
        selectedPageId: String?,
        selectedSectionId: String?,
        selectedQuestionId: String?
    ) -> Unit
) {
    val state = viewModel.state
    val listState = rememberLazyListState()
    var isMonitorScreen by remember { mutableStateOf(false) }
    var fillWidth by remember { mutableStateOf(1f) }
    val navigationEventState = rememberNavigationEventState<NavigationEventInfo>(
        currentInfo = NavigationEventInfo.None,
    )
    val defaultPadding = dimension.spacing.space4

    LaunchedEffect(Unit) {
        setupContent { isMonitorScreenContent, fillWidthContent ->
            isMonitorScreen = isMonitorScreenContent
            fillWidth = fillWidthContent
        }
    }

    NavigationBackHandler(state = navigationEventState, onBackCompleted = {
        onClose(state.template, null, null, null)
    })

    Box(
        modifier = Modifier.fillMaxSize()
            .background(color.bg.normal)
            .padding(top = paddingValues.calculateTopPadding()),
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Row(
                modifier = Modifier.fillMaxWidth()
                    .background(color.surface.normal)
                    .padding(dimension.spacing.space4),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(dimension.spacing.space3)
            ) {
                IconButton(
                    icon = Res.drawable.ic_arrow_left_line,
                    backgroundColor = Color.Transparent,
                    tint = color.icon.subtle,
                    modifier = Modifier.size(dimension.size.size7),
                    onClick = {
                        onClose(state.template, null, null, null)
                    }
                )

                InputSearchText(
                    placeholder = stringResource(Res.string.search_this_audit),
                    modifier = Modifier
                        .weight(1f)
                        .align(Alignment.CenterVertically),
                    onTextChanged = { state.setOnKeywordValueChanged(it) }
                )
            }

            DefaultHorizontalDivider()

            if (state.keyword.isNotEmpty()) {
                Column(modifier = Modifier.fillMaxWidth(fillWidth)) {
                    InspectionSearchResultTab(
                        activeTab = state.activeTab,
                        modifier = Modifier.align(Alignment.Start)
                            .padding(
                                vertical = dimension.spacing.space2,
                                horizontal = defaultPadding
                            ),
                        onClick = {
                            state.onChangeTab(it)
                        }
                    )

                    Row(
                        modifier = Modifier.align(Alignment.Start)
                            .padding(
                                vertical = dimension.spacing.space2,
                                horizontal = defaultPadding
                            )
                    ) {
                        val label = if (state.totalResult > 0) stringResource(
                            Res.string.search_result,
                            state.totalResult,
                            state.keyword
                        ) else stringResource(Res.string.search_no_result, state.keyword)
                        TextBodySmall(
                            annotatedString = label.highlightBoldKeyword(state.keyword),
                            color = { it.subtle }
                        )
                    }

                    DefaultHorizontalDivider()
                }
            }

            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxHeight()
                    .fillMaxWidth()
                    .padding(
                        bottom = paddingValues.calculateBottomPadding()
                    ),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (state.keyword.isEmpty()) {
                    item {
                        val text = stringResource(Res.string.initial_search_question)
                        TextBodySmall(
                            text = text,
                            color = { it.subtle },
                            textAlign = TextAlign.Center,
                            modifier = Modifier
                                .fillMaxWidth(fillWidth)
                                .padding(vertical = defaultPadding)
                        )
                    }
                    return@LazyColumn
                }

                when (state.activeTab) {
                    InspectionSearchQuestionType.QUESTION -> {
                        items(state.displayData ?: emptyList()) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth(fillWidth)
                                    .background(color.surface.normal)
                                    .animateItem(),
                            ) {
                                TextBodyTiny(
                                    annotatedString = it.pageTitle.highlightKeyword(state.keyword),
                                    color = { textColor -> textColor.subtle },
                                    modifier = Modifier.padding(
                                        top = dimension.spacing.space5,
                                        start = defaultPadding,
                                        end = defaultPadding
                                    )
                                )
                                TextBodyTiny(
                                    annotatedString = it.sectionTitle.highlightKeyword(state.keyword),
                                    color = { textColor -> textColor.subtle },
                                    bold = true,
                                    modifier = Modifier.padding(
                                        horizontal = defaultPadding,
                                        vertical = dimension.spacing.space2
                                    )
                                )
                                ContentItem(
                                    keyword = state.keyword,
                                    topPadding = dimension.spacing.space1,
                                    showLinkQuestion = true,
                                    onGoToQuestion = {
                                        onClose(state.template, null, null, it.question.id)
                                    },
                                    props = ContentItemProps(
                                        showActionButton = true,
                                        isInternalAudit = state.isInternalAudit,
                                        item = it.question,
                                        vesselList = state.vessels,
                                        onChooseAnswer = { answerId ->
                                            state.setAnswerOption(
                                                questionId = it.question.id,
                                                answerOptionId = answerId
                                            )
                                        },
                                        onClickVessel = { id, value ->
                                            state.setAnswerVessel(
                                                questionId = it.question.id,
                                                vesselId = id,
                                                vesselName = value
                                            )
                                        },
                                        onSetAnswerTextChanged = { value ->
                                            state.setAnswerTextChanged(
                                                questionId = it.question.id,
                                                text = value
                                            )
                                        },
                                        onDeleteNotes = {
                                            state.onSaveNotes(
                                                questionId = it.question.id,
                                                notes = ""
                                            )
                                        },
                                        onDeleteFiles = {
                                            state.onSaveFiles(
                                                questionId = it.question.id,
                                                files = emptyList()
                                            )
                                        },
                                        onShowModalGuidance = {
                                            state.onShowModalGuidance(true, it.question)
                                        },
                                        onShowModalCalendar = {
                                            state.onShowModalDate(
                                                show = true,
                                                questionId = it.question.id,
                                                dateIso = it.question.answer.answerDateIso,
                                                title = it.question.questionText
                                            )
                                        },
                                        onShowModalLocation = {
                                            state.onShowModalLocation(
                                                show = true,
                                                questionId = it.question.id,
                                                name = it.question.answer.answerLocation.name
                                            )
                                        },
                                        onShowModalNotes = {
                                            state.onShowModalNotes(
                                                show = true,
                                                questionId = it.question.id,
                                                notes = it.question.answer.notes
                                            )
                                        },
                                        onShowModalFiles = {
                                            state.onShowModalMedia(
                                                show = true,
                                                questionId = it.question.id,
                                                files = it.question.answer.files
                                            )
                                        },
                                        onShowFindingCategory = { findingCategory ->
                                            state.onShowModalFindingCategory(
                                                show = true,
                                                questionId = it.question.id,
                                                findingCategory = findingCategory
                                            )
                                        }
                                    )
                                )

                                DefaultHorizontalDivider()
                            }
                        }
                    }

                    InspectionSearchQuestionType.PAGE -> {
                        if (state.totalResult > 0) {
                            stickyHeader {
                                TextBodyTiny(
                                    text = InspectionSearchQuestionType.PAGE.name.toTitleCase(),
                                    color = { it.subtle },
                                    modifier = Modifier
                                        .fillMaxWidth(fillWidth)
                                        .padding(
                                            top = defaultPadding,
                                            start = defaultPadding,
                                        )
                                )
                            }
                        }
                        items(state.displayPageData ?: emptyList()) {
                            InspectionSearchResultItem(
                                keyword = state.keyword,
                                resultData = it,
                                modifier = Modifier
                                    .fillMaxWidth(fillWidth)
                                    .padding(horizontal = defaultPadding),
                                onClick = {
                                    onClose(
                                        state.template, it, null, null
                                    )
                                }
                            )
                        }
                    }

                    InspectionSearchQuestionType.SECTION -> {
                        if (state.totalResult > 0) {
                            stickyHeader {
                                TextBodyTiny(
                                    text = InspectionSearchQuestionType.SECTION.name.toTitleCase(),
                                    color = { it.subtle },
                                    modifier = Modifier
                                        .fillMaxWidth(fillWidth)
                                        .padding(
                                            top = defaultPadding,
                                            start = defaultPadding,
                                        )
                                )
                            }
                        }
                        items(state.displaySectionData ?: emptyList()) {
                            InspectionSearchResultItem(
                                keyword = state.keyword,
                                resultData = it,
                                modifier = Modifier
                                    .fillMaxWidth(fillWidth)
                                    .padding(horizontal = defaultPadding),
                                onClick = {
                                    onClose(
                                        state.template, null, it, null
                                    )
                                }
                            )
                        }
                    }
                }

                item {
                    Spacer(modifier = Modifier.height(dimension.spacing.space12))
                }
            }
        }

        ImagePickerModal(
            title = stringResource(Res.string.add_files),
            description = stringResource(Res.string.maximum_file, Constant.MAX_FILES),
            files = state.modalFiles.files,
            show = state.modalFiles.show,
            onDismiss = {
                state.onShowModalMedia(false)
            },
            onSave = {
                state.onSaveModalFiles(it)
            }
        )

        NotesModal(
            notes = state.modalNotes.notes,
            show = state.modalNotes.show,
            onDismiss = {
                state.onShowModalNotes(show = false)
            },
            onSave = {
                state.onSaveModalNotes(it)
            }
        )

        FindingCategoryModal(
            title = stringResource(Res.string.select_category),
            show = state.modalFindingCategory.show,
            selectedFindingCategory = state.modalFindingCategory.findingCategory,
            referenceItems = state.findingCategoryOptions.map {
                SelectionModel(
                    id = it.name,
                    value = it.toVariant().label
                )
            },
            onDismiss = {
                state.onShowModalFindingCategory(show = false)
            },
            onSelectedFindingCategory = {
                state.onSaveModalFindingCategory(it)
            }
        )

        DateTimePickerModal(
            title = state.modalDate.title,
            dateIsoUtc0 = state.modalDate.dateIso,
            show = state.modalDate.show,
            onDismiss = {
                state.onShowModalDate(show = false, questionId = state.modalDate.questionId)
            },
            onSave = {
                state.onSaveModalDate(it)
            }
        )

        LocationModal(
            title = stringResource(Res.string.select_location),
            location = state.modalLocation.name,
            show = state.modalLocation.show,
            onDismiss = {
                state.onShowModalLocation(show = false)
            },
            onSave = { name, location ->
                state.onSaveModalLocation(name, location)
            }
        )

        GuidanceModal(
            show = state.modalGuidance.show,
            question = state.modalGuidance.question?.questionText ?: "",
            guidance = state.modalGuidance.question?.guidelines ?: "",
            onDismiss = {
                state.onShowModalGuidance(false)
            }
        )
    }
}
